// clearedto.rent/quote (+ /renter, /cfi): the direct-to-pilot door
// (CTR-DIRECT-RENTER-AND-MOBILE-SPEC + Design round 3). Same buy flow as a
// school portal with NO school floor. Step 1 order per Design: school/club
// typeahead FIRST (optional, skip link directly under it), then the CFI
// toggle, then aircraft types (SEL + gliders permanently included; no prices
// on step 1, types are rater inputs). A claimed school match redirects to
// its portal; an unclaimed match attributes the quote to its dashboard;
// free text attributes and queues for seeding; skip leaves attribution
// blank. /cfi presets the instructor toggle.
import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api.js';
import Footer from '@/components/Footer.jsx';
import Wordmark from '@/components/Wordmark.jsx';
import AboutYou from '@/routes/portal/AboutYou.jsx';
import Coverage from '@/routes/portal/Coverage.jsx';
import CheckoutGate from '@/routes/portal/CheckoutGate.jsx';
import Outcome, { FinalizingBlock } from '@/routes/portal/Outcome.jsx';
import { useQuoteHandoff, selectionFrom } from '@/lib/useQuoteHandoff.js';

const STEP_LABELS = ['Where you fly', 'About you', 'Your coverage', 'Secure checkout'];

const TYPES = [
  ['SEL', 'Single engine land + gliders', 'Included in every policy.', true],
  ['SES', 'Seaplane', 'Single engine sea. Add it if you fly floats.', false],
  ['MEL', 'Multi engine land', 'Twins. Add it if you rent or instruct in one.', false],
  ['rotorcraft', 'Rotorcraft', 'Helicopters and gyros.', false],
];

function portalUrlFor(slug) {
  const host = window.location.hostname.toLowerCase();
  if (host === 'clearedto.rent' || host.endsWith('.clearedto.rent')) return `https://${slug}.clearedto.rent`;
  return `/?school=${encodeURIComponent(slug)}`;
}

export default function Quote({ cfiPreset = false }) {
  const [step, setStep] = useState(1);
  const [isCfi, setIsCfi] = useState(cfiPreset);
  const [types, setTypes] = useState({ SES: false, MEL: false, rotorcraft: false });
  const [typeInfo, setTypeInfo] = useState(null);
  const [schoolQuery, setSchoolQuery] = useState('');
  const [schoolHits, setSchoolHits] = useState([]);
  const [attribution, setAttribution] = useState(null); // {school_slug,name} | {school_text} | null
  const [prefilledFrom, setPrefilledFrom] = useState(null);
  const [answers, setAnswers] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const handoff = useQuoteHandoff(setOutcome);
  const [checkoutSel, setCheckoutSel] = useState(null);
  const [licensedStates, setLicensedStates] = useState(null);
  const [acksOn, setAcksOn] = useState(false);
  const [ackGate, setAckGate] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    api.get('/api/ctr/config').then((r) => {
      setLicensedStates(r.licensed_states ?? null);
      setAcksOn(!!(r.checkout_acks && r.checkout_acks.mode === 'on'));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const q = schoolQuery.trim();
    if (q.length < 2) { setSchoolHits([]); return undefined; }
    const my = ++seq.current;
    const t = setTimeout(() => {
      api.get(`/api/ctr/schools/search?q=${encodeURIComponent(q)}`)
        .then((r) => { if (seq.current === my) setSchoolHits(r.schools || []); })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [schoolQuery]);

  async function pickSchool(s) {
    if (s.claimed) { window.location.href = portalUrlFor(s.slug); return; }
    setAttribution({ school_slug: s.slug, name: s.name });
    setSchoolQuery(s.name);
    setSchoolHits([]);
    // An unclaimed seed has no fleet on file; a claimed pick never reaches
    // here. Fleet prefill applies when the seed carries types.
    try {
      const r = await api.get(`/api/ctr/schools/by-slug/${encodeURIComponent(s.slug)}`);
      const t = r.school?.aircraft_types || {};
      if (t.SES || t.MEL || t.rotorcraft) {
        setTypes({ SES: !!t.SES, MEL: !!t.MEL, rotorcraft: !!t.rotorcraft });
        setPrefilledFrom(s.name);
      }
    } catch { /* attribution stands either way */ }
  }

  function applyFreeText() {
    const text = schoolQuery.trim();
    if (!text) return;
    setAttribution({ school_text: text });
    setSchoolHits([]);
  }

  async function getPrice(a) {
    setAnswers(a); setQuoteError(null); setQuoting(true);
    try {
      const r = await api.post('/api/ctr/quote/direct', {
        attribution: attribution || null,
        is_cfi: isCfi || a.is_cfi === true,
        aircraft_types: types,
        answers: a,
      });
      if (r.outcome === 'quoted') { setQuote(r); setStep(3); }
      else setOutcome(r.outcome);
    } catch (e) {
      if (e.code === 'school_is_claimed' && e.body?.redirect_slug) { window.location.href = portalUrlFor(e.body.redirect_slug); return; }
      setQuoteError(e.code === 'quoting_unavailable'
        ? 'Quoting opens shortly. Your answers are good; try again soon.'
        : "We couldn't reach the carrier just now. Give it a minute and try again.");
    } finally { setQuoting(false); }
  }

  const attributedName = attribution?.name || attribution?.school_text || null;
  const showStepper = !outcome;
  const effStep = Math.min(step, 4);
  const cfiFlag = isCfi;

  return (
    <div className="mx-auto flex min-h-screen max-w-[520px] flex-col bg-paper shadow-shell">
      <header className="bg-primary-deep p-[20px_22px] text-white">
        <a href="/" className="text-[19px]"><Wordmark className="text-white" /></a>
        <div className="mt-1 text-[11.5px] opacity-75">Renter and CFI insurance, direct. Coverage by Global Aerospace · Arranged by NDEMNIFLY Aviation Insurance</div>
      </header>

      {showStepper ? (
        <div className="bg-paper p-[16px_22px_4px]">
          {/* Slim progress bar with the step name below sm (mobile spec). */}
          <div className="sm:hidden">
            <div className="h-1 overflow-hidden rounded-pill bg-line">
              <div className="h-1 rounded-pill bg-primary transition-all" style={{ width: `${(effStep / STEP_LABELS.length) * 100}%` }} />
            </div>
            <div className="mt-1.5 text-[11px] font-bold tracking-[0.02em] text-ink">Step {effStep} of {STEP_LABELS.length} · {STEP_LABELS[effStep - 1]}</div>
          </div>
          <div className="hidden gap-1.5 sm:flex">
            {STEP_LABELS.map((label, i) => (
              <div key={label} className="flex-1">
                <div className={`h-1 rounded-pill ${i + 1 < effStep ? 'bg-ok-fg' : i + 1 === effStep ? 'bg-primary' : 'bg-line'}`} />
                <div className={`mt-1.5 text-[10px] font-bold leading-[1.3] tracking-[0.02em] ${i + 1 <= effStep ? 'text-ink' : 'text-ink-faint'}`}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <main className="flex flex-1 flex-col p-[18px_22px_40px]">
        {outcome === 'pending' ? (
          <div className="my-auto py-[30px]">
            <div className="mb-4 text-center text-sm text-ink-dim">Getting your secure payment page from Global Aerospace.</div>
            <FinalizingBlock />
          </div>
        ) : outcome ? (
          <Outcome
            outcome={outcome}
            school={attributedName ? { name: attributedName } : handoff.info.school}
            email={answers?.email || handoff.info.email || 'your email'}
            onRestart={() => { handoff.forget(); setOutcome(null); setStep(1); }}
            onContinuePayment={handoff.startWatching}
            onBackToCoverage={() => { handoff.stopWatching(); setOutcome(null); if (quote) setStep(3); else { handoff.forget(); setStep(1); } }}
            summaryUrl={handoff.info.summaryUrl}
            watching={handoff.watching}
            handoffError={handoff.info.error}
            statusLabel={handoff.info.label}
            effectiveDate={handoff.info.effectiveDate}
          />
        ) : step === 1 ? (
          <>
            <h1 className="mb-1.5 mt-1.5 text-[23px] font-bold leading-[1.2] tracking-[-0.02em]">Where do you fly?</h1>
            <p className="mb-4 text-sm leading-[1.55] text-ink-dim">
              Name your school or club and your certificate lands on their dashboard automatically. Totally optional.
            </p>
            <div className="relative mb-1">
              <input
                value={schoolQuery}
                onChange={(e) => { setSchoolQuery(e.target.value); setAttribution(null); }}
                placeholder="School or club name, or airport code"
                className="min-h-[48px] w-full rounded-[11px] border-[1.5px] border-line bg-white px-3.5 text-ink outline-none focus:border-accent"
              />
              {schoolHits.length && !attribution ? (
                <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 rounded-card-lg border border-line bg-white py-1 shadow-float">
                  {schoolHits.map((s) => (
                    <button key={s.slug} type="button" onMouseDown={(e) => { e.preventDefault(); pickSchool(s); }}
                      className="flex w-full items-center justify-between gap-2 px-3.5 py-2.5 text-left hover:bg-paper">
                      <span className="min-w-0">
                        <span className="block truncate text-[13.5px] font-semibold">{s.name}</span>
                        <span className="text-[11px] text-ink-faint">{[s.home_airport, s.state].filter(Boolean).join(' · ')}</span>
                      </span>
                      <span className={`flex-none rounded-pill px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.04em] ${s.claimed ? 'bg-teal-fill text-primary' : 'bg-fill text-ink-dim'}`}>
                        {s.claimed ? 'on the platform' : 'listed'}
                      </span>
                    </button>
                  ))}
                  <button type="button" onMouseDown={(e) => { e.preventDefault(); applyFreeText(); }}
                    className="w-full px-3.5 py-2.5 text-left text-[12.5px] font-semibold text-accent hover:bg-paper">
                    Use &quot;{schoolQuery.trim()}&quot; as typed
                  </button>
                </div>
              ) : null}
            </div>
            {attribution ? (
              <div className="mb-1 rounded-card bg-teal-faint p-[9px_12px] text-[12.5px] text-teal-mute">
                {attribution.school_slug
                  ? <>Your quote will be tracked for <b>{attribution.name}</b> (requirements not yet confirmed by {attribution.name}).</>
                  : <>We&apos;ll note <b>{attribution.school_text}</b> and get their page set up.</>}
              </div>
            ) : null}
            {schoolQuery.trim().length >= 2 && !attribution && !schoolHits.length ? (
              <button type="button" onClick={applyFreeText} className="mb-1 text-left text-[12.5px] font-semibold text-accent">
                No match. Use &quot;{schoolQuery.trim()}&quot; as typed
              </button>
            ) : null}
            <button type="button" onClick={() => { setAttribution(null); setSchoolQuery(''); setStep(2); }}
              className="mb-5 self-start text-[12.5px] font-semibold text-ink-dim underline">
              Skip: I rent from a few places / not sure
            </button>

            <label className="mb-5 flex min-h-[48px] cursor-pointer items-center gap-2.5 rounded-[11px] border-[1.5px] border-line bg-white p-[11px_14px] text-[13.5px] font-semibold text-ink">
              <input type="checkbox" className="h-[17px] w-[17px]" checked={isCfi} onChange={(e) => setIsCfi(e.target.checked)} />
              I&apos;m a flight instructor
            </label>

            <div className="mb-1.5 text-[13px] font-bold">What do you fly? Check all that apply.</div>
            <div className="mb-2 flex flex-col gap-[7px]">
              {TYPES.map(([key, label, blurb, locked]) => {
                const on = locked || types[key];
                return (
                  <div key={key}>
                    <div className={`flex min-h-[48px] w-full items-center gap-[11px] rounded-[11px] border-[1.5px] p-[11px_14px] ${on ? 'border-accent bg-teal-card' : 'border-line bg-white'} ${locked ? 'opacity-95' : ''}`}>
                      <button type="button" disabled={locked} aria-label={label}
                        onClick={() => setTypes((t) => ({ ...t, [key]: !t[key] }))}
                        className={`flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[4px] border-[1.5px] text-[12px] font-bold text-white ${on ? 'border-accent bg-accent' : 'border-line-strong bg-white'}`}>
                        {on ? '✓' : ''}
                      </button>
                      <button type="button" onClick={() => setTypeInfo(typeInfo === key ? null : key)} className="flex-1 text-left text-[13.5px] font-semibold">
                        {label} <span className="text-[11px] text-line-strong">ⓘ</span>
                      </button>
                      {locked ? <span className="flex-none rounded-pill bg-fill px-2 py-[3px] text-[9.5px] font-bold uppercase tracking-[0.04em] text-ink-dim">included in every policy</span> : null}
                    </div>
                    {typeInfo === key ? <div className="mt-1 rounded-lg bg-teal-faint p-[8px_11px] text-xs leading-[1.5] text-teal-mute">{blurb}</div> : null}
                  </div>
                );
              })}
            </div>
            {prefilledFrom ? <div className="mb-2 text-[11.5px] text-ink-faint">Started from {prefilledFrom}&apos;s fleet.</div> : null}
            <div className="mb-5 text-[12.5px] leading-[1.55] text-ink-faint">
              Types set your base rate, so your price comes after the next step.
            </div>
            <button type="button" onClick={() => setStep(2)}
              className="min-h-[52px] w-full rounded-card-lg bg-primary text-base font-bold text-white hover:bg-primary-deep">
              Continue
            </button>
          </>
        ) : step === 2 ? (
          <AboutYou
            school={attribution?.school_slug ? { slug: attribution.school_slug, name: attribution.name, aircraft_types: {} } : null}
            licensedStates={licensedStates}
            quotingEnabled={!quoting}
            quoteError={quoteError}
            cfiPreset={cfiFlag}
            onSubmit={getPrice}
            onBack={() => setStep(1)}
          />
        ) : ackGate ? (
          <CheckoutGate
            quoteToken={quote?.quote_token}
            cfiExtension={!!(ackGate.cfiOn && quote?.cfi_extension)}
            types={Object.keys(checkoutSel?.types || {})}
            onComplete={() => { setAckGate(null); handoff.checkout(quote?.quote_token, checkoutSel); }}
            onBack={() => setAckGate(null)}
          />
        ) : (
          <Coverage
            flow="direct"
            school={attributedName ? { name: attributedName } : null}
            quote={quote}
            answers={answers}
            electedTypes={{ SEL: true, ...types }}
            onCheckout={(selection) => {
              const sel = selectionFrom(selection, quote);
              setCheckoutSel(sel);
              if (acksOn && quote?.quote_token) { setAckGate({ cfiOn: selection?.cfiOn }); return 'handled'; }
              handoff.checkout(quote?.quote_token, sel);
              return 'handled';
            }}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}
